const TOKEN_KEY = "ddsign_token";
const CLIENT_ID = "dd-sign-word-addin";
const REDIRECT_URI = "https://localhost:3000/auth/callback";

let baseUrl = "https://staging.api.ddsign.ae/api/v1";

export function setBaseUrl(url: string): void {
  baseUrl = url;
}

function generateRandomString(length: number): string {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(36).padStart(2, "0"))
    .join("")
    .substring(0, length);
}

async function sha256(plain: string): Promise<ArrayBuffer> {
  const encoder = new TextEncoder();
  const data = encoder.encode(plain);
  return crypto.subtle.digest("SHA-256", data);
}

function base64UrlEncode(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  bytes.forEach((byte) => (binary += String.fromCharCode(byte)));
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export async function generateCodeChallenge(verifier: string): Promise<string> {
  const hash = await sha256(verifier);
  return base64UrlEncode(hash);
}

export function generateCodeVerifier(): string {
  return generateRandomString(64);
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function storeToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export function isAuthenticated(): boolean {
  return getToken() !== null;
}

/**
 * Start the OAuth PKCE login flow.
 * Opens a popup to the DD-Sign login page.
 * Returns a promise that resolves with the access token.
 */
export async function startLogin(loginUrl: string): Promise<string> {
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  const state = generateRandomString(16);

  // Store verifier and state for the callback
  localStorage.setItem("pkce_verifier", codeVerifier);
  localStorage.setItem("pkce_state", state);

  const authUrl =
    `${loginUrl}?` +
    `addin=true&` +
    `client_id=${CLIENT_ID}&` +
    `redirect_uri=${encodeURIComponent(REDIRECT_URI)}&` +
    `code_challenge=${codeChallenge}&` +
    `code_challenge_method=S256&` +
    `state=${state}`;

  return new Promise((resolve, reject) => {
    const popup = window.open(authUrl, "ddsign-login", "width=500,height=700");

    if (!popup) {
      reject(new Error("Popup blocked. Please allow popups for this site."));
      return;
    }

    const handleMessage = async (event: MessageEvent) => {
      if (event.data?.type !== "ddsign-auth-callback") return;

      window.removeEventListener("message", handleMessage);

      const { code, state: returnedState, error } = event.data;

      if (error) {
        reject(new Error(error));
        return;
      }

      const savedState = localStorage.getItem("pkce_state");
      if (returnedState !== savedState) {
        reject(new Error("State mismatch. Please try again."));
        return;
      }

      try {
        const response = await fetch(`${baseUrl}/oauth/pkce/token`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            grant_type: "authorization_code",
            code,
            code_verifier: codeVerifier,
            client_id: CLIENT_ID,
            redirect_uri: REDIRECT_URI,
          }),
        });

        if (!response.ok) {
          throw new Error("Token exchange failed");
        }

        const data = await response.json();
        storeToken(data.access_token);
        resolve(data.access_token);
      } catch (err) {
        reject(err);
      } finally {
        localStorage.removeItem("pkce_verifier");
        localStorage.removeItem("pkce_state");
        popup.close();
      }
    };

    window.addEventListener("message", handleMessage);

    // Poll for popup close (user closed without completing)
    const pollTimer = setInterval(() => {
      if (popup.closed) {
        clearInterval(pollTimer);
        window.removeEventListener("message", handleMessage);
        reject(new Error("Login cancelled"));
      }
    }, 1000);
  });
}

export function logout(): void {
  clearToken();
}
